import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

export const pdfService = {
  /**
   * Generate PDF document for seeker intake form
   * @param {Object} formData - The intake form data
   * @param {String} pathway - The selected pathway ('quick' or 'comprehensive')
   * @returns {jsPDF} PDF document instance
   */
  generateIntakePDF(formData, pathway) {
    const doc = new jsPDF();
    const pageWidth = doc?.internal?.pageSize?.getWidth();
    const pageHeight = doc?.internal?.pageSize?.getHeight();
    let yPosition = 20;

    // Helper function to add section header
    const addSectionHeader = (title) => {
      if (yPosition > pageHeight - 30) {
        doc?.addPage();
        yPosition = 20;
      }
      doc?.setFontSize(14);
      doc?.setFont(undefined, 'bold');
      doc?.text(title, 14, yPosition);
      yPosition += 8;
      doc?.setFont(undefined, 'normal');
      doc?.setFontSize(10);
    };

    // Helper function to add field
    const addField = (label, value) => {
      if (yPosition > pageHeight - 20) {
        doc?.addPage();
        yPosition = 20;
      }
      if (value && value !== '' && value !== false) {
        const displayValue = Array.isArray(value) ? value?.join(', ') : String(value);
        const lines = doc?.splitTextToSize(`${label}: ${displayValue}`, pageWidth - 28);
        doc?.text(lines, 14, yPosition);
        yPosition += lines?.length * 5 + 2;
      }
    };

    // Header
    doc?.setFontSize(18);
    doc?.setFont(undefined, 'bold');
    doc?.text('Seeker Intake Form - Progress Report', 14, yPosition);
    yPosition += 10;
    doc?.setFontSize(10);
    doc?.setFont(undefined, 'normal');
    doc?.text(`Pathway: ${pathway === 'quick' ? 'Quick Start' : 'Comprehensive Questionnaire'}`, 14, yPosition);
    yPosition += 6;
    doc?.text(`Generated: ${new Date()?.toLocaleString()}`, 14, yPosition);
    yPosition += 12;

    // Demographic Information (Comprehensive only)
    if (pathway === 'comprehensive' && (formData?.preferredName || formData?.ageRange || formData?.gender)) {
      addSectionHeader('Basic Information');
      addField('Preferred Name', formData?.preferredName);
      addField('Age Range', formData?.ageRange);
      addField('Gender', formData?.gender);
      addField('Population Segment', formData?.populationSegment);
      addField('Languages', formData?.languages);
      yPosition += 4;
    }

    // Cultural Preferences (Comprehensive only)
    if (pathway === 'comprehensive' && (formData?.culturalBackground || formData?.religion)) {
      addSectionHeader('Cultural Preferences');
      addField('Cultural Background', formData?.culturalBackground);
      addField('Religion', formData?.religion);
      addField('Prefer Same Culture', formData?.preferSameCulture ? 'Yes' : 'No');
      addField('Open to Any Culture', formData?.openToAnyCulture ? 'Yes' : 'No');
      yPosition += 4;
    }

    // Current Issues
    if (formData?.currentIssues?.length > 0 || formData?.symptoms?.length > 0) {
      addSectionHeader('Current Issues & Symptoms');
      addField('Current Issues', formData?.currentIssues);
      addField('Symptoms', formData?.symptoms);
      addField('Duration', formData?.duration);
      if (formData?.additionalContext) {
        if (yPosition > pageHeight - 30) {
          doc?.addPage();
          yPosition = 20;
        }
        doc?.text('Additional Context:', 14, yPosition);
        yPosition += 5;
        const contextLines = doc?.splitTextToSize(formData?.additionalContext, pageWidth - 28);
        doc?.text(contextLines, 14, yPosition);
        yPosition += contextLines?.length * 5 + 2;
      }
      addField('Has Existing Conditions', formData?.hasExistingConditions ? 'Yes' : 'No');
      addField('Currently in Treatment', formData?.currentlyInTreatment ? 'Yes' : 'No');
      yPosition += 4;
    }

    // Severity Assessment
    if (formData?.urgencyLevel || formData?.impactLevel) {
      addSectionHeader('Urgency Assessment');
      addField('Urgency Level', formData?.urgencyLevel);
      addField('Impact Level', formData?.impactLevel);
      addField('Self-Harm Thoughts', formData?.selfHarmThoughts ? 'Yes' : 'No');
      addField('Harm to Others', formData?.harmToOthers ? 'Yes' : 'No');
      yPosition += 4;
    }

    // Helper Preferences
    if (formData?.helperType || formData?.sessionFormats?.length > 0) {
      addSectionHeader('Helper Preferences');
      addField('Helper Type', formData?.helperType);
      addField('Preferred Location', formData?.preferredLocation);
      addField('Session Formats', formData?.sessionFormats);
      addField('Prefer Same Gender', formData?.preferSameGender ? 'Yes' : 'No');
      addField('Open to Any Gender', formData?.openToAnyGender ? 'Yes' : 'No');
      addField('Require Specific Expertise', formData?.requireExpertise ? 'Yes' : 'No');
      addField('Expand Search Radius', formData?.expandSearchRadius ? 'Yes' : 'No');
      yPosition += 4;
    }

    // Consent & Privacy
    if (formData?.agreeToTerms || formData?.dataConsent) {
      addSectionHeader('Consent & Privacy Settings');
      addField('Agreed to Terms', formData?.agreeToTerms ? 'Yes' : 'No');
      addField('Understand Limitations', formData?.understandLimitations ? 'Yes' : 'No');
      
      if (formData?.dataConsent) {
        doc?.text('Data Consent:', 14, yPosition);
        yPosition += 5;
        addField('  Matching Algorithm', formData?.dataConsent?.matching ? 'Yes' : 'No');
        addField('  Analytics', formData?.dataConsent?.analytics ? 'Yes' : 'No');
        addField('  Research', formData?.dataConsent?.research ? 'Yes' : 'No');
        addField('  Communications', formData?.dataConsent?.communications ? 'Yes' : 'No');
      }
      
      if (formData?.privacySettings) {
        doc?.text('Privacy Settings:', 14, yPosition);
        yPosition += 5;
        addField('  Anonymous Display', formData?.privacySettings?.anonymousDisplay ? 'Yes' : 'No');
        addField('  Hide from Directory', formData?.privacySettings?.hideFromDirectory ? 'Yes' : 'No');
        addField('  Require Approval', formData?.privacySettings?.requireApproval ? 'Yes' : 'No');
      }
      yPosition += 4;
    }

    // Voice Dictation Transcript
    if (formData?.voiceDictationTranscript) {
      addSectionHeader('Voice Dictation Transcript');
      if (yPosition > pageHeight - 30) {
        doc?.addPage();
        yPosition = 20;
      }
      const transcriptLines = doc?.splitTextToSize(formData?.voiceDictationTranscript, pageWidth - 28);
      doc?.text(transcriptLines, 14, yPosition);
      yPosition += transcriptLines?.length * 5 + 4;
    }

    // Context Media URLs
    if (formData?.contextMediaUrls?.length > 0) {
      addSectionHeader('Uploaded Media References');
      formData?.contextMediaUrls?.forEach((url, index) => {
        addField(`Media ${index + 1}`, url);
      });
      yPosition += 4;
    }

    // Footer
    const totalPages = doc?.internal?.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc?.setPage(i);
      doc?.setFontSize(8);
      doc?.setTextColor(128);
      doc?.text(
        `Page ${i} of ${totalPages} | Confidential - For authorized use only`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );
    }

    return doc;
  },

  /**
   * Download PDF to user's device
   * @param {Object} formData - The intake form data
   * @param {String} pathway - The selected pathway
   */
  downloadIntakePDF(formData, pathway) {
    const doc = this.generateIntakePDF(formData, pathway);
    const fileName = `intake-progress-${new Date()?.toISOString()?.split('T')?.[0]}.pdf`;
    doc?.save(fileName);
  },

  /**
   * Get PDF as blob for email attachment
   * @param {Object} formData - The intake form data
   * @param {String} pathway - The selected pathway
   * @returns {Blob} PDF blob
   */
  getIntakePDFBlob(formData, pathway) {
    const doc = this.generateIntakePDF(formData, pathway);
    return doc?.output('blob');
  }
};
